import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Gallery.css";

export default function Gallery() {
  const navigate = useNavigate();

  const [data, setData] = useState({ title: "Highlights", home_count: 12, items: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/content/gallery.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json || {});
      } catch (e) {
        console.error("Failed to load gallery.json", e);
        if (!cancelled) setErr("Couldn't load gallery. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const allItems = Array.isArray(data?.items) ? data.items : [];
  const homeCount = Number(data?.home_count) || 12;
  const photos = allItems.slice(0, homeCount);

  // Lightbox
  const [idx, setIdx] = useState(-1);
  const isOpen = idx > -1;
  const close = useCallback(() => setIdx(-1), []);
  const next = useCallback(() => setIdx((i) => (i + 1) % photos.length), [photos.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + photos.length) % photos.length), [photos.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close, next, prev]);

  // Preload adjacent images
  useEffect(() => {
    if (!isOpen || photos.length <= 1) return;
    const nextIdx = (idx + 1) % photos.length;
    const prevIdx = (idx - 1 + photos.length) % photos.length;
    [nextIdx, prevIdx].forEach((i) => {
      const img = new Image();
      img.src = photos[i].image;
    });
  }, [idx, isOpen, photos]);

  return (
    <section id="gallery" className="gal-section">
      <style>{`
        #gallery {
          scroll-margin-top: 96px;
        }

        .gal-section {
          background: linear-gradient(180deg, #fff9fb 0%, #fff9fb 100%);
          padding: 120px 24px 80px;
          position: relative;
          overflow: hidden;
          scroll-margin-top: 96px;
        }

        .gal-container {
          max-width: 980px;
          margin: 0 auto;
        }

        .gal-header {
          text-align: center;
          margin-bottom: 42px;
        }

        .gal-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(176, 53, 76, 0.1);
          color: #b0354c;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 16px;
          letter-spacing: 0.5px;
        }

        .gal-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 4vw, 3rem);
          color: #670d2b;
          margin: 0;
          line-height: 1.2;
        }

        .gal-subtitle {
          color: #666;
          font-size: 1.1rem;
          margin-top: 12px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Home highlights grid */
        .masonry {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }

        @media (max-width: 1024px) {
          .masonry { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 640px) {
          .masonry { grid-template-columns: 1fr; }
        }

        .masonry-item {
          position: relative;
          border-radius: 18px;
          overflow: hidden;
          cursor: pointer;
          background: #f0f0f0;
          transform: translateZ(0);
        }

        .masonry-item img {
          width: 100%;
          height: 260px;
          object-fit: cover;
          display: block;
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .masonry-item:hover img {
          transform: scale(1.08);
        }

        .masonry-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(103, 13, 43, 0.9) 0%, transparent 60%);
          opacity: 0;
          transition: opacity 0.4s ease;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 24px;
        }

        .masonry-item:hover .masonry-overlay {
          opacity: 1;
        }

        .masonry-caption {
          color: #fff;
          font-size: 1.1rem;
          font-weight: 600;
          transform: translateY(20px);
          transition: transform 0.4s ease;
        }

        .masonry-item:hover .masonry-caption {
          transform: translateY(0);
        }

        .masonry-tags {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 8px;
          transform: translateY(20px);
          transition: transform 0.4s ease 0.1s;
        }

        .masonry-item:hover .masonry-tags {
          transform: translateY(0);
        }

        .masonry-tags em {
          font-style: normal;
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.9);
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 12px;
          border-radius: 20px;
          backdrop-filter: blur(10px);
        }

        /* Skeleton Loading */
        .skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 16px;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* Explore Button */
        .explore-more {
          text-align: center;
          margin-top: 60px;
        }

        .btn-explore {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 16px 32px;
          background: linear-gradient(135deg, #b0354c 0%, #670d2b 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(176, 53, 76, 0.3);
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .btn-explore:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(176, 53, 76, 0.4);
        }

        .btn-explore svg {
          transition: transform 0.3s ease;
        }

        .btn-explore:hover svg {
          transform: translateX(5px);
        }

        /* Lightbox */
        .lightbox {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(20px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
        }

        .lightbox-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .lb-img {
          max-width: 100%;
          max-height: 80vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
        }

        .lb-btn {
          position: absolute;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: #fff;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          z-index: 10;
        }

        .lb-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }

        .lb-close {
          top: -60px;
          right: 0;
        }

        .lb-prev {
          left: -80px;
          top: 50%;
          transform: translateY(-50%);
        }

        .lb-prev:hover {
          transform: translateY(-50%) scale(1.1);
        }

        .lb-next {
          right: -80px;
          top: 50%;
          transform: translateY(-50%);
        }

        .lb-next:hover {
          transform: translateY(-50%) scale(1.1);
        }

        .lb-meta {
          margin-top: 20px;
          text-align: center;
          color: #fff;
          max-width: 600px;
        }

        .lb-caption {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .lb-tags {
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .lb-tags em {
          font-style: normal;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.8);
          background: rgba(255, 255, 255, 0.1);
          padding: 6px 16px;
          border-radius: 20px;
        }

        .lb-counter {
          position: absolute;
          top: -40px;
          left: 0;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          #gallery { scroll-margin-top: 84px; }
          .gal-section { padding: 90px 16px 56px; }
          .masonry-item img { height: 220px; }
          .lb-btn { width: 44px; height: 44px; }
          .lb-close { top: -50px; right: 0; }
          .lb-prev { left: 10px; }
          .lb-next { right: 10px; }
          .lightbox { padding: 20px; }
        }
      `}</style>

      <div className="gal-container">
        <div className="gal-header">
          <motion.div 
            className="gal-kicker"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            ✨ Our Portfolio
          </motion.div>
          <motion.h2 
            className="gal-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {data?.title || "Highlights"}
          </motion.h2>
          <motion.p 
            className="gal-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Capturing moments of joy, love, and celebration from our most memorable events
          </motion.p>
        </div>

        {loading && (
          <div className="masonry">
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                className="masonry-item skeleton"
                key={i}
                style={{ height: 280 + ((i * 47) % 150) }}
              />
            ))}
          </div>
        )}

        {err && (
          <motion.div 
            className="text-center text-danger mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {err}
          </motion.div>
        )}

        {!loading && !err && photos.length === 0 && (
          <div className="text-center text-muted mb-4">No photos yet.</div>
        )}

        <div className="masonry">
          <AnimatePresence>
            {photos.map((item, i) => (
              <motion.figure
                className="masonry-item"
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setIdx(i)}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <img
                  src={item.image}
                  alt={item.alt || item.caption || `Photo ${i + 1}`}
                  loading="lazy"
                />
                <div className="masonry-overlay">
                  {(item.caption || (item.tags && item.tags.length > 0)) && (
                    <>
                      {item.caption && (
                        <div className="masonry-caption">{item.caption}</div>
                      )}
                      {item.tags && item.tags.length > 0 && (
                        <div className="masonry-tags">
                          {item.tags.slice(0, 3).map((t, k) => (
                            <em key={k}>#{t}</em>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.figure>
            ))}
          </AnimatePresence>
        </div>

        {allItems.length > photos.length && (
          <motion.div 
            className="explore-more"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <button className="btn-explore" onClick={() => navigate("/gallery-full")}>
              Explore Full Gallery
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && photos[idx] && (
          <motion.div 
            className="lightbox" 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <div className="lightbox-content">
              <span className="lb-counter">{idx + 1} / {photos.length}</span>
              
              <button className="lb-btn lb-close" aria-label="Close" onClick={close}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>

              {photos.length > 1 && (
                <>
                  <button 
                    className="lb-btn lb-prev" 
                    aria-label="Previous"
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                  <button 
                    className="lb-btn lb-next" 
                    aria-label="Next"
                    onClick={(e) => { e.stopPropagation(); next(); }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </>
              )}

              <motion.img
                className="lb-img"
                src={photos[idx].image}
                alt={photos[idx].alt || photos[idx].caption || "Photo"}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              />

              {(photos[idx].caption || photos[idx].tags?.length) && (
                <motion.div 
                  className="lb-meta" 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {photos[idx].caption && (
                    <div className="lb-caption">{photos[idx].caption}</div>
                  )}
                  {photos[idx].tags?.length > 0 && (
                    <div className="lb-tags">
                      {photos[idx].tags.map((t, k) => <em key={k}>#{t}</em>)}
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}